import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService, OrgUser } from '../../services/api';
import { CsvCell, downloadCsv } from '../../utils/csvUtils';
import MetricCard from './components/MetricCard';
import BarChart from './components/BarChart';
import HorizBar from './components/HorizBar';
import Heatmap from './components/Heatmap';
import { useChartTooltip } from './components/ChartTooltip';
import {
  NormalizedQuery, NormalizedVersion,
  deriveMetrics, deriveVolumeByDay, deriveOperationMix, deriveGDSUsage,
  deriveGraphDemand, deriveHeatmap, deriveVersionActivity,
  deriveUserRows, deriveUserMetrics, deriveAdoption,
} from './utils/analyticsData';
import './Analytics.css';

const QUERY_LIMIT = 3000;

/** Format a Date as a local datetime-local input value (YYYY-MM-DDTHH:mm) */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      width="13" height="13" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
      style={spinning ? { animation: 'an-spin 0.7s linear infinite' } : undefined}
    >
      <path d="M2 8a6 6 0 1 0 1.2-3.6M2 2v4h4" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M2.5 13.5h11" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ color: 'var(--an-text-muted)' }}>
      <path d="M3 5l3 3 3-3" />
    </svg>
  );
}

// Normalize a single raw item from listVersions into NormalizedVersion.
// The API returns fields at root OR nested under .attributes — handle both.
function normalizeVersion(item: any): NormalizedVersion | null {
  const a = item.attributes ?? item;
  const version_id = String(a.version_id ?? item.id ?? '');
  if (!version_id) return null;
  return {
    version_id,
    published:   a.published   ?? false,
    gds_enabled: a.gds_enabled ?? false,
    created_at:  a.created_at  ? Number(a.created_at) : undefined,
  };
}

// Normalize a single raw item from listQueries into NormalizedQuery.
// Attributes live at item.attributes ?? item. timestamp is epoch ms.
function normalizeQuery(item: any, versionId: string): NormalizedQuery | null {
  const a = item.attributes ?? item;
  const query_id = String(a.query_id ?? item.id ?? '');
  if (!query_id) return null;
  return {
    query_id,
    query_string: a.query_string ?? '',
    status:       a.status       ?? '',
    timestamp:    Number(a.timestamp ?? 0),
    long_running: Boolean(a.long_running ?? false),
    version_id:   versionId,
    instance_id:  a.instance_id ?? undefined,
    user_id:      a.user_id     ?? undefined,
  };
}

// Fetch all pages for one status value, aborting early if limit exceeded.
async function fetchByStatus(
  kgId: string,
  versionId: string,
  status: string,
  startMs?: number,
  endMs?: number,
  onCount?: (n: number) => void,
  signal?: AbortSignal,
  userId?: string,
): Promise<NormalizedQuery[]> {
  const acc: NormalizedQuery[] = [];
  let nextToken: string | undefined;

  do {
    if (signal?.aborted) break;
    const res: any = await apiService.listQueries(kgId, versionId, {
      pageSize:      100,
      status,
      includeUserId: true,
      startDate: startMs ? Math.floor(startMs / 1000) : undefined,
      endDate:   endMs   ? Math.floor(endMs   / 1000) : undefined,
      nextToken,
      userId,
    });

    const raw: any[] = Array.isArray(res) ? res : (res?.data ?? res?.queries ?? []);
    for (const item of raw) {
      const q = normalizeQuery(item, versionId);
      // Client-side guard in case the API ignores the user_id param
      if (q && (!userId || String(q.user_id ?? '') === userId)) acc.push(q);
    }
    onCount?.(acc.length);

    nextToken = res?.meta?.next_token ?? res?.next_token ?? undefined;
  } while (nextToken && !signal?.aborted);

  return acc;
}

// Fetch all statuses in parallel for one version and merge, deduplicating by query_id.
const ALL_STATUSES = ['COMPLETED', 'FAILED', 'QUEUED', 'IN_PROGRESS', 'TIMED_OUT'];

async function fetchAllQueries(
  kgId: string,
  versionId: string,
  startMs?: number,
  endMs?: number,
  onCount?: (total: number) => void,
  signal?: AbortSignal,
  userId?: string,
): Promise<{ queries: NormalizedQuery[]; limited: boolean }> {
  let totalSeen = 0;
  let limited = false;
  const limitController = new AbortController();

  // Combine external cancel signal with the internal limit controller so either
  // one aborts all in-flight page fetches.
  const signals = [limitController.signal, ...(signal ? [signal] : [])];
  const combinedSignal = signals.length > 1
    ? (AbortSignal as any).any?.(signals) ?? limitController.signal
    : limitController.signal;

  // Fallback: if AbortSignal.any isn't available, forward external abort manually
  if (signal && !(AbortSignal as any).any) {
    signal.addEventListener('abort', () => limitController.abort(), { once: true });
  }

  const results = await Promise.allSettled(
    ALL_STATUSES.map(s => fetchByStatus(kgId, versionId, s, startMs, endMs,
      (n) => {
        totalSeen += n;
        onCount?.(totalSeen);
        if (totalSeen > QUERY_LIMIT && !limitController.signal.aborted) {
          limited = true;
          limitController.abort();
        }
      },
      combinedSignal,
      userId,
    ))
  );

  const seen = new Set<string>();
  const acc: NormalizedQuery[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled') {
      for (const q of r.value) {
        if (!seen.has(q.query_id)) {
          seen.add(q.query_id);
          acc.push(q);
        }
      }
    }
  }
  return { queries: acc, limited };
}

const LIVE_INTERVAL_MS = 30_000;

const Analytics: React.FC = () => {
  // kg_ids from listKGs (item.kg_id at root)
  const [kgIds, setKgIds]           = useState<string[]>([]);
  const [selectedKg, setSelectedKg] = useState<string>('');
  const [kgLoading, setKgLoading]   = useState(true);

  const [versions, setVersions]         = useState<NormalizedVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [kgOpen, setKgOpen]             = useState(false);
  const [versionOpen, setVersionOpen]   = useState(false);
  const kgDropdownRef     = useRef<HTMLDivElement | null>(null);
  const versionDropdownRef = useRef<HTMLDivElement | null>(null);
  const userDropdownRef   = useRef<HTMLDivElement | null>(null);

  // Organisation users (for the User filter dropdown)
  const [orgId, setOrgId]               = useState<string>(() => sessionStorage.getItem('polly_org_id') ?? '');
  const [orgUsers, setOrgUsers]         = useState<OrgUser[]>([]);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const [userFilterId, setUserFilterId] = useState<string>('');   // '' = all users
  const [userOpen, setUserOpen]         = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [queries, setQueries]           = useState<NormalizedQuery[]>([]);
  const [loading, setLoading]           = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [error, setError]               = useState('');

  const [customFrom, setCustomFrom]   = useState<string>(() => toDatetimeLocal(new Date(Date.now() - 60 * 60 * 1000)));
  const [customTo, setCustomTo]       = useState<string>(() => toDatetimeLocal(new Date()));
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [liveActive, setLiveActive]   = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [queryLimited, setQueryLimited] = useState(false);
  const [hasFetched, setHasFetched]     = useState(false);

  const abortRef     = useRef<AbortController | null>(null);
  const liveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runOutcomesTooltip = useChartTooltip();

  // ── Load KG list once on mount ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.listKGs();
        const items: any[] = Array.isArray(res) ? res : (res?.data ?? []);
        // kg_id is at root level (confirmed from KGList.tsx usage)
        const seen = new Set<string>();
        const ids: string[] = [];
        for (const item of items) {
          const id = String(item.kg_id ?? item.attributes?.kg_id ?? item.id ?? '');
          if (id && !seen.has(id)) { seen.add(id); ids.push(id); }
        }
        setKgIds(ids);
        if (ids.length > 0) setSelectedKg(ids[0]);
        // Fallback org id source: org_id on the KG list items
        if (!sessionStorage.getItem('polly_org_id')) {
          const org = items.map(i => i?.org_id ?? i?.attributes?.org_id).find(Boolean);
          if (org) setOrgId(String(org));
        }
      } catch {
        setError('Failed to load knowledge graphs.');
      } finally {
        setKgLoading(false);
      }
    })();
  }, []);

  // ── Keep org id in sync with Layout's sessionStorage updates ────────────
  useEffect(() => {
    const handler = () => setOrgId(sessionStorage.getItem('polly_org_id') ?? '');
    window.addEventListener('pollyOrgIdUpdated', handler);
    return () => window.removeEventListener('pollyOrgIdUpdated', handler);
  }, []);

  // ── Load all users in the organisation ──────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setOrgUsersLoading(true);
    apiService.listOrgUsers(orgId)
      .then(users => {
        if (cancelled) return;
        const label = (u: OrgUser) => `${u.firstName} ${u.lastName}`.trim() || u.email || u.id;
        setOrgUsers(users.sort((a, b) => label(a).localeCompare(label(b))));
      })
      .catch(() => { if (!cancelled) setOrgUsers([]); })
      .finally(() => { if (!cancelled) setOrgUsersLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Core fetch ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async (
    kgId: string, versionId: string,
    fromStr: string, toStr: string,
    isRefresh = false,
    userId = '',
  ) => {
    if (!kgId) return;

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    setQueryLimited(false);
    setHasFetched(false);

    try {
      // 1. Fetch versions
      const vRes: any = await apiService.listVersions(kgId);
      const rawVItems: any[] = Array.isArray(vRes) ? vRes : (vRes?.data ?? []);
      const normalizedVersions: NormalizedVersion[] = rawVItems
        .map(normalizeVersion)
        .filter((v): v is NormalizedVersion => v !== null);
      setVersions(normalizedVersions);

      // 2. Resolve version
      const publishedVersions = normalizedVersions.filter(v => v.published);
      const latestPublished = publishedVersions[publishedVersions.length - 1]?.version_id ?? '';
      const latestVersionId = latestPublished || (normalizedVersions[normalizedVersions.length - 1]?.version_id ?? '');
      const targetVersionId = versionId || latestVersionId;
      if (!versionId && latestVersionId) setSelectedVersionId(latestVersionId);

      const fetchStartMs = new Date(fromStr).getTime();
      const fetchEndMs   = new Date(toStr).getTime();

      const { queries: allQueries, limited } = targetVersionId
        ? await fetchAllQueries(kgId, targetVersionId, fetchStartMs, fetchEndMs, undefined, abortRef.current.signal, userId || undefined)
        : { queries: [], limited: false };

      if (limited) setQueryLimited(true);
      setQueries(allQueries);
      setLastUpdated(new Date());
      setHasFetched(true);
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setError('Failed to load analytics data.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // ── Reset version to latest when KG changes ─────────────────────────────
  useEffect(() => { setSelectedVersionId(''); }, [selectedKg]);

  // ── Close dropdowns on outside click ────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (kgDropdownRef.current && !kgDropdownRef.current.contains(e.target as Node)) setKgOpen(false);
      if (versionDropdownRef.current && !versionDropdownRef.current.contains(e.target as Node)) setVersionOpen(false);
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Re-fetch when KG, version or user changes (use current date range) ───
  useEffect(() => {
    if (selectedKg) fetchData(selectedKg, selectedVersionId, customFrom, customTo, false, userFilterId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKg, selectedVersionId, userFilterId, fetchData]);

  // ── Live auto-refresh every 30 s ────────────────────────────────────────
  useEffect(() => {
    if (liveActive && selectedKg) {
      liveTimerRef.current = setInterval(() => {
        fetchData(selectedKg, selectedVersionId, customFrom, customTo, true, userFilterId);
      }, LIVE_INTERVAL_MS);
    } else {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    }
    return () => {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    };
  }, [liveActive, selectedKg, customFrom, customTo, selectedVersionId, userFilterId, fetchData]);

  // ── Derive display data — all filtering is client-side ──────────────────
  const startMs = new Date(customFrom).getTime();
  const endMs   = new Date(customTo).getTime();

  // 1. Filter by time period (timestamp is epoch ms)
  const queriesInPeriod = queries.filter(q => q.timestamp >= startMs && q.timestamp <= endMs);

  // 2. Filter to the selected version (always exactly one version)
  const filteredVersions = versions.filter(v => v.version_id === selectedVersionId);
  // 3. Filter to the selected organisation user (if any)
  const filteredQueries  = queriesInPeriod.filter(q =>
    q.version_id === selectedVersionId &&
    (!userFilterId || String(q.user_id ?? '') === userFilterId)
  );

  const userLabel = (u: OrgUser) => `${u.firstName} ${u.lastName}`.trim() || u.email || u.id;
  const selectedOrgUser = orgUsers.find(u => u.id === userFilterId);
  const userSearchLc = userSearch.trim().toLowerCase();
  const visibleOrgUsers = userSearchLc
    ? orgUsers.filter(u => `${userLabel(u)} ${u.email} ${u.id}`.toLowerCase().includes(userSearchLc))
    : orgUsers;

  const metrics         = deriveMetrics(filteredQueries, filteredVersions);
  const volumeData      = deriveVolumeByDay(filteredQueries, startMs, endMs);
  const operationMix    = deriveOperationMix(filteredQueries);
  const gdsUsage        = deriveGDSUsage(filteredQueries);
  const graphDemand     = deriveGraphDemand(filteredQueries);
  const heatmapData     = deriveHeatmap(filteredQueries);
  const versionActivity = deriveVersionActivity(filteredQueries, filteredVersions);
  const userRows        = deriveUserRows(filteredQueries, filteredQueries.length);
  const userMetrics     = deriveUserMetrics(filteredQueries, queries, startMs);
  const adoptionData    = deriveAdoption(filteredQueries, queries, startMs, endMs);

  const isSpinning = loading || refreshing;

  // ── Export every query currently shown (KG + version + time range + user) ──
  const handleDownloadCsv = () => {
    const usersById = new Map<string, OrgUser>(orgUsers.map(u => [u.id, u]));
    const headers = [
      'Query ID', 'Timestamp', 'Status', 'Long Running', 'KG ID', 'Version',
      'User ID', 'User Name', 'User Email', 'Instance ID', 'Query',
    ];
    const rows: CsvCell[][] = [...filteredQueries]
      .sort((a, b) => b.timestamp - a.timestamp)
      .map(q => {
        const uid = q.user_id ? String(q.user_id) : '';
        const u = usersById.get(uid);
        return [
          q.query_id,
          q.timestamp ? new Date(q.timestamp).toISOString() : '',
          q.status,
          q.long_running ? 'Yes' : 'No',
          selectedKg,
          q.version_id,
          uid,
          u ? `${u.firstName} ${u.lastName}`.trim() : '',
          u?.email ?? '',
          q.instance_id ?? '',
          q.query_string,
        ];
      });
    const stamp = (s: string) => s.replace(/[:T]/g, '-');
    const userPart = userFilterId ? `_user-${userFilterId}` : '';
    downloadCsv(
      `kg-analytics_${selectedKg}_v${selectedVersionId}${userPart}_${stamp(customFrom)}_to_${stamp(customTo)}.csv`,
      headers,
      rows,
    );
  };

  return (
    <div className="an-root">

      {/* ── Sticky filter bar ──────────────────────────────────────────────── */}
      <div className="an-filter-bar">

        {/* KG ID selector */}
        <div className="an-dropdown" ref={kgDropdownRef}>
          <button
            className="an-dropdown-trigger"
            onClick={() => { setKgOpen(o => !o); setVersionOpen(false); }}
            disabled={kgLoading}
          >
            <span className="an-filter-pill-muted">KG</span>
            <span className="an-dropdown-value">
              {kgLoading ? 'Loading…' : (selectedKg || '—')}
            </span>
            <ChevronIcon />
          </button>
          {kgOpen && (
            <div className="an-dropdown-menu">
              {kgIds.map(id => (
                <button
                  key={id}
                  className={`an-dropdown-item${selectedKg === id ? ' an-dropdown-item--active' : ''}`}
                  onClick={() => { setSelectedKg(id); setKgOpen(false); }}
                >
                  {id}
                  {selectedKg === id && <span className="an-dropdown-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Version selector */}
        <div className="an-dropdown" ref={versionDropdownRef}>
          <button
            className="an-dropdown-trigger"
            onClick={() => { setVersionOpen(o => !o); setKgOpen(false); }}
            disabled={loading || versions.length === 0}
          >
            <span className="an-filter-pill-muted">Version</span>
            <span className="an-dropdown-value">
              {loading ? '—' : selectedVersionId ? `v${selectedVersionId}` : '—'}
            </span>
            <ChevronIcon />
          </button>
          {versionOpen && (
            <div className="an-dropdown-menu">
              {versions
                .filter(v => v.published)
                .sort((a, b) => Number(b.version_id) - Number(a.version_id))
                .map(v => (
                  <button
                    key={v.version_id}
                    className={`an-dropdown-item${selectedVersionId === v.version_id ? ' an-dropdown-item--active' : ''}`}
                    onClick={() => { setSelectedVersionId(v.version_id); setVersionOpen(false); }}
                  >
                    <span>v{v.version_id}</span>
                    {selectedVersionId === v.version_id && <span className="an-dropdown-check">✓</span>}
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Custom date-time range */}
        <div className="an-custom-range">
          <span className="an-filter-pill-muted an-custom-range-label">From</span>
          <input
            type="datetime-local"
            className="an-datetime-input"
            value={customFrom}
            max={customTo}
            onChange={e => setCustomFrom(e.target.value)}
          />
          <span className="an-filter-pill-muted an-custom-range-label">To</span>
          <input
            type="datetime-local"
            className="an-datetime-input"
            value={customTo}
            min={customFrom}
            max={toDatetimeLocal(new Date())}
            onChange={e => setCustomTo(e.target.value)}
          />
          <button
            className="an-custom-apply-btn"
            disabled={isSpinning || !selectedKg}
            onClick={() => fetchData(selectedKg, selectedVersionId, customFrom, customTo, false, userFilterId)}
          >
            Apply
          </button>
        </div>

      {/* Organisation user selector */}
        <div className="an-dropdown" ref={userDropdownRef}>
          <button
            className="an-dropdown-trigger"
            onClick={() => { setUserOpen(o => !o); setKgOpen(false); setVersionOpen(false); }}
            disabled={orgUsersLoading || !orgId}
            title={selectedOrgUser?.email}
          >
            <span className="an-filter-pill-muted">User</span>
            <span className="an-dropdown-value">
              {orgUsersLoading ? 'Loading…' : selectedOrgUser ? userLabel(selectedOrgUser) : userFilterId || 'All users'}
            </span>
            <ChevronIcon />
          </button>
          {userOpen && (
            <div className="an-dropdown-menu" style={{ maxHeight: 320, overflowY: 'auto', minWidth: 260 }}>
              <div style={{ padding: 6, position: 'sticky', top: 0, background: 'inherit', zIndex: 1 }}>
                <input
                  autoFocus
                  type="text"
                  className="an-datetime-input"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  placeholder={`Search ${orgUsers.length} users…`}
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                />
              </div>
              <button
                className={`an-dropdown-item${!userFilterId ? ' an-dropdown-item--active' : ''}`}
                onClick={() => { setUserFilterId(''); setSelectedUserId(null); setUserOpen(false); }}
              >
                All users
                {!userFilterId && <span className="an-dropdown-check">✓</span>}
              </button>
              {visibleOrgUsers.length === 0 ? (
                <div className="an-empty" style={{ padding: '12px 0' }}>No users found</div>
              ) : visibleOrgUsers.map(u => (
                <button
                  key={u.id}
                  className={`an-dropdown-item${userFilterId === u.id ? ' an-dropdown-item--active' : ''}`}
                  onClick={() => { setUserFilterId(u.id); setSelectedUserId(null); setUserOpen(false); }}
                  title={`${u.email} · ${u.id}`}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                    <span>{userLabel(u)}</span>
                    {u.id && u.id !== userLabel(u) && (
                      <span style={{ fontSize: 11, color: 'var(--an-text-muted)' }}>{u.id}</span>
                    )}
                  </span>
                  {userFilterId === u.id && <span className="an-dropdown-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="an-filter-sep" />

        <div className="an-filter-right">
          {/* Cancel button — only visible while loading */}
          {isSpinning && (
            <button
              className="an-cancel-btn"
              onClick={() => { abortRef.current?.abort(); setLoading(false); setRefreshing(false); }}
            >
              Cancel
            </button>
          )}

          {/* Refresh button */}
          <button
            className="an-refresh-btn"
            onClick={() => fetchData(selectedKg, selectedVersionId, customFrom, customTo, true, userFilterId)}
            disabled={isSpinning || !selectedKg}
            title="Refresh data"
          >
            <RefreshIcon spinning={isSpinning} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>

          {/* Download CSV button — exports all queries matching current filters */}
          <button
            className="an-refresh-btn"
            onClick={handleDownloadCsv}
            disabled={isSpinning || filteredQueries.length === 0}
            title={`Download ${filteredQueries.length} queries as CSV`}
          >
            <DownloadIcon />
            Download CSV
          </button>

          <span className="an-header-time" style={{ opacity: isSpinning ? 0.4 : 1 }}>
            {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }) : (isSpinning ? '—:—:— —' : '')}
          </span>
        </div>
      </div>

      {/* Query limit warning — too many requests, shorten range */}
      {queryLimited && (
        <div className="an-limit-banner an-limit-banner--warn">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M8 1L15 14H1L8 1z" /><line x1="8" y1="6" x2="8" y2="9" /><circle cx="8" cy="12" r="0.5" fill="currentColor" />
          </svg>
          <span>
            <strong>Too many queries ({QUERY_LIMIT.toLocaleString()}+ found)</strong> — results are cut off.
            Shorten the time range to a few hours or days to see accurate data.
          </span>
          <button className="an-limit-dismiss" onClick={() => setQueryLimited(false)}>✕</button>
        </div>
      )}

      {/* No data after fetch */}
      {hasFetched && !loading && !queryLimited && queries.length === 0 && (
        <div className="an-limit-banner an-limit-banner--info">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="8" cy="8" r="7" /><line x1="8" y1="5" x2="8" y2="8" /><circle cx="8" cy="11" r="0.5" fill="currentColor" />
          </svg>
          <span>
            <strong>No queries found</strong> in this time range.
            Try selecting a wider range or a different version.
          </span>
        </div>
      )}

      {/* Error */}
      {error && <div className="an-error-banner">{error}</div>}

      {/* ── Main scrollable content ─────────────────────────────────────────── */}
      <div className="an-page">

        {/* Metric cards */}
        <div className="an-metrics-row">
          <MetricCard
            label="Total Queries"
            value={loading ? <span className="an-skeleton" style={{ width: 48, height: 29, display: 'block' }} /> : metrics.total}
            subtext={
              loading ? null :
              metrics.total > 0
                ? <span style={{ color: '#24CF35', fontWeight: 500 }}>{metrics.daysActive} active day{metrics.daysActive !== 1 ? 's' : ''}</span>
                : 'No data'
            }
          />
          <MetricCard
            label="Completed"
            value={
              loading
                ? <span className="an-skeleton" style={{ width: 48, height: 29, display: 'block' }} />
                : <span style={{ color: metrics.completed > 0 ? '#1E9E2B' : undefined }}>{metrics.completed}</span>
            }
            subtext={
              loading ? null :
              metrics.total > 0
                ? `${Math.round((metrics.completed / metrics.total) * 100)}% of runs`
                : '0% of runs'
            }
          />
          <MetricCard
            label="Failed"
            value={
              loading
                ? <span className="an-skeleton" style={{ width: 48, height: 29, display: 'block' }} />
                : <span style={{ color: metrics.failed > 0 ? '#FF004D' : undefined }}>{metrics.failed}</span>
            }
            subtext={
              loading ? null :
              metrics.total > 0
                ? `${((metrics.failed / metrics.total) * 100).toFixed(1)}% error rate`
                : '0.0% error rate'
            }
          />
          <MetricCard
            label="Timed Out"
            value={
              loading
                ? <span className="an-skeleton" style={{ width: 48, height: 29, display: 'block' }} />
                : <span style={{ color: metrics.timedOut > 0 ? '#F59E0B' : undefined }}>{metrics.timedOut}</span>
            }
            subtext={
              loading ? null :
              metrics.total > 0
                ? <span style={{ color: metrics.timedOut > 0 ? '#C46A05' : '#6B6480', fontWeight: metrics.timedOut > 0 ? 500 : undefined }}>
                    {((metrics.timedOut / metrics.total) * 100).toFixed(1)}% of runs
                  </span>
                : '0.0% of runs'
            }
          />
          <MetricCard
            label="Long Running"
            value={
              loading
                ? <span className="an-skeleton" style={{ width: 48, height: 29, display: 'block' }} />
                : <span style={{ color: metrics.longRunning > 0 ? '#C46A05' : undefined }}>{metrics.longRunning}</span>
            }
            subtext={
              loading ? null :
              metrics.total > 0
                ? <span style={{ color: metrics.longRunning > 0 ? '#C46A05' : '#6B6480', fontWeight: metrics.longRunning > 0 ? 500 : undefined }}>
                    {((metrics.longRunning / metrics.total) * 100).toFixed(1)}% flagged
                  </span>
                : '0.0% flagged'
            }
          />
        </div>

        {/* Charts row */}
        <div className="an-charts-row">

          {/* Stacked bar chart */}
          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div className="an-card-title">Query volume by version</div>
                <div className="an-card-sub">Queries per day grouped by version</div>
              </div>
              <div className="an-legend-row">
                {volumeData.series.filter(s => s.label !== 'no data').map(s => (
                  <span key={s.label} className="an-legend-item">
                    <span className="an-legend-swatch" style={{ background: s.color }} />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
            {loading ? (
              <div className="an-skeleton" style={{ height: 256, borderRadius: 8 }} />
            ) : queries.length === 0 ? (
              <div className="an-empty">No query data in this period</div>
            ) : (
              <BarChart
                dateLabels={volumeData.dateLabels}
                series={volumeData.series}
                maxValue={volumeData.maxValue}
              />
            )}
          </div>

        </div>

        {/* Analysis row */}
        <div className="an-analysis-row">

          {/* Run outcomes — Plotly-style SVG bar chart */}
          <div className="an-card">
            <div>
              <div className="an-card-title">Run outcomes</div>
              <div className="an-card-sub">{metrics.total} total runs</div>
            </div>
            {loading ? (
              <div className="an-skeleton" style={{ flex: 1, borderRadius: 8 }} />
            ) : (() => {
              const bars = [
                { label: 'Completed',      count: metrics.completed,  color: '#24CF35' },
                { label: 'Failed',         count: metrics.failed,     color: '#FF004D' },
                { label: 'Timed out',      count: metrics.timedOut,   color: '#F59E0B' },
                { label: 'Running/queued', count: metrics.running,    color: '#3C5DE2' },
              ];
              const W = 240, H = 180;
              const PAD = { top: 16, right: 12, bottom: 36, left: 36 };
              const chartW = W - PAD.left - PAD.right;
              const chartH = H - PAD.top - PAD.bottom;
              const maxVal = Math.max(1, ...bars.map(b => b.count));
              // nice y-axis max
              const niceTop = (() => {
                const mag = Math.pow(10, Math.floor(Math.log10(maxVal)));
                const n = maxVal / mag;
                const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
                return nice * mag;
              })();
              const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(niceTop * f));
              const barW = Math.floor(chartW / bars.length * 0.5);
              const gap  = chartW / bars.length;
              return (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {runOutcomesTooltip.tooltipEl}
                  <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: 'visible', maxHeight: 200 }}>
                    {/* Gridlines + y-axis ticks */}
                    {yTicks.map((v, i) => {
                      const y = PAD.top + chartH - (v / niceTop) * chartH;
                      return (
                        <g key={i}>
                          <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y}
                            stroke={v === 0 ? '#D1CBE0' : '#EDE9F4'} strokeWidth={v === 0 ? 1.2 : 1} />
                          <text x={PAD.left - 5} y={y + 3.5}
                            textAnchor="end" fontSize={9} fontFamily="var(--font-mono)" fill="var(--an-text-faint)">
                            {v}
                          </text>
                        </g>
                      );
                    })}
                    {/* Bars */}
                    {bars.map((bar, i) => {
                      const barH = Math.max(bar.count > 0 ? 3 : 0, (bar.count / niceTop) * chartH);
                      const x = PAD.left + i * gap + (gap - barW) / 2;
                      const y = PAD.top + chartH - barH;
                      const sharePct = metrics.total > 0 ? Math.round((bar.count / metrics.total) * 100) : 0;
                      // Top users for this status
                      const statusKey = bar.label === 'Running/queued' ? ['RUNNING', 'IN_PROGRESS', 'QUEUED'] : [bar.label.toUpperCase().replace(' ', '_')];
                      const topUsers = Object.entries(
                        filteredQueries
                          .filter(q => statusKey.some(k => q.status.toUpperCase() === k))
                          .reduce<Record<string, number>>((acc, q) => {
                            const uid = q.user_id ?? q.instance_id ?? 'unknown';
                            acc[uid] = (acc[uid] ?? 0) + 1;
                            return acc;
                          }, {})
                      )
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 4);
                      return (
                        <g key={bar.label}>
                          {/* Invisible hit area for tooltip */}
                          {bar.count > 0 && (
                            <rect
                              x={x} y={PAD.top} width={barW} height={chartH}
                              fill="transparent"
                              style={{ cursor: 'crosshair' }}
                              onMouseEnter={(e) => runOutcomesTooltip.show({
                                title: bar.label,
                                subtitle: `${bar.count} queries · ${sharePct}% of total`,
                                rowsHeader: topUsers.length > 0 ? 'User ID' : undefined,
                                rows: topUsers.map(([uid, cnt]) => ({
                                  label: uid,
                                  value: cnt,
                                  color: bar.color,
                                })),
                              }, e)}
                              onMouseMove={runOutcomesTooltip.move}
                              onMouseLeave={runOutcomesTooltip.hide}
                            />
                          )}
                          {/* Bar */}
                          <rect x={x} y={y} width={barW} height={barH}
                            rx={4} ry={4} fill={bar.color} opacity={0.9}
                            style={{ pointerEvents: 'none' }}
                          />
                          {/* Count label above bar */}
                          {bar.count > 0 && (
                            <text x={x + barW / 2} y={y - 5}
                              textAnchor="middle" fontSize={11} fontWeight="600"
                              fontFamily="var(--font-mono)" fill="var(--an-text-primary)"
                              style={{ pointerEvents: 'none' }}>
                              {bar.count}
                            </text>
                          )}
                          {/* % label */}
                          {bar.count > 0 && (
                            <text x={x + barW / 2} y={y - 16}
                              textAnchor="middle" fontSize={9.5}
                              fontFamily="var(--font-mono)" fill="var(--an-text-muted)"
                              style={{ pointerEvents: 'none' }}>
                              {sharePct}%
                            </text>
                          )}
                          {/* X-axis label */}
                          <text x={x + barW / 2} y={PAD.top + chartH + 14}
                            textAnchor="middle" fontSize={9.5}
                            fontFamily="var(--font-primary)" fill="var(--an-text-secondary)"
                            style={{ pointerEvents: 'none' }}>
                            {bar.label.split('/')[0]}
                          </text>
                          {bar.label.includes('/') && (
                            <text x={x + barW / 2} y={PAD.top + chartH + 25}
                              textAnchor="middle" fontSize={9.5}
                              fontFamily="var(--font-primary)" fill="var(--an-text-secondary)"
                              style={{ pointerEvents: 'none' }}>
                              {bar.label.split('/')[1]}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                </div>
              );
            })()}
          </div>

          {/* Operation mix */}
          <div className="an-card">
            <div>
              <div className="an-card-title">What is being asked</div>
              <div className="an-card-sub">Leading operation, parsed from query text</div>
            </div>
            {loading ? (
              <div className="an-skeleton" style={{ height: 160, borderRadius: 8 }} />
            ) : operationMix.length === 0 ? (
              <div className="an-empty" style={{ padding: '24px 0' }}>No queries in period</div>
            ) : (
              <div className="an-card-scroll">
                <div className="an-horiz-group">
                  {operationMix.map(op => (
                    <div key={op.label} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div className="an-op-label-row">
                        <span className="an-op-label">{op.label}</span>
                        <span className="an-op-count">{op.count} · {op.pct}%</span>
                      </div>
                      <div style={{ height: 7, borderRadius: 9999, background: '#F2EEF8' }}>
                        <div style={{ width: `${Math.max(op.pct, 1)}%`, height: 7, borderRadius: 9999, background: op.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* GDS usage */}
          <div className="an-card">
            <div>
              <div className="an-card-title">Graph data science usage</div>
              <div className="an-card-sub">
                {gdsUsage.length > 0
                  ? `${gdsUsage.reduce((s, g) => s + g.count, 0)} GDS calls · ${gdsUsage.length} procedure${gdsUsage.length !== 1 ? 's' : ''}`
                  : 'No GDS calls in period'}
              </div>
            </div>
            {loading ? (
              <div className="an-skeleton" style={{ height: 160, borderRadius: 8 }} />
            ) : gdsUsage.length === 0 ? (
              <div className="an-empty" style={{ padding: '24px 0' }}>No GDS calls detected</div>
            ) : (
              <div className="an-card-scroll">
                <div className="an-horiz-group--tight">
                  {gdsUsage.map(g => (
                    <HorizBar key={g.label} label={g.label} count={g.count} pct={g.pct} color={g.color} labelWidth={160} barHeight={7} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Graph demand */}
          <div className="an-card">
            <div>
              <div className="an-card-title">Parts of the graph in demand</div>
              <div className="an-card-sub">Labels and relationships named in queries</div>
            </div>
            {loading ? (
              <div className="an-skeleton" style={{ height: 160, borderRadius: 8 }} />
            ) : graphDemand.nodes.length === 0 && graphDemand.rels.length === 0 ? (
              <div className="an-empty" style={{ padding: '24px 0' }}>No labels or types found</div>
            ) : (
              <div className="an-card-scroll">
                <div className="an-horiz-group--tight">
                  {graphDemand.nodes.map(n => (
                    <HorizBar key={n.label} label={n.label} count={n.count} pct={n.pct} color={n.color} labelWidth={140} barHeight={7} labelColor="#7433CC" />
                  ))}
                </div>
                {graphDemand.rels.length > 0 && (
                  <>
                    <div className="an-divider" />
                    <div className="an-horiz-group--tight">
                      {graphDemand.rels.map(r => (
                        <HorizBar key={r.label} label={r.label} count={r.count} pct={r.pct} color={r.color} labelWidth={140} barHeight={7} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Who is using it ─────────────────────────────────────────────── */}
        <div className="an-who-header">
          <span className="an-who-title">Who is using it</span>
          <span className="an-who-summary">
            {loading ? '' : `${userMetrics.totalUsers} people · ${userMetrics.serviceAccounts} service account${userMetrics.serviceAccounts !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* User metric cards — 2 cards: Active Users + Service Accounts */}
        <div className="an-who-metrics-row">
          {/* Active users */}
          <div className="an-metric-card an-metric-card--large">
            <div className="an-metric-label">Active users</div>
            <div className="an-metric-value">
              {loading ? <span className="an-skeleton" style={{ width: 48, height: 36, display: 'block' }} /> : userMetrics.activeUsers}
            </div>
            {!loading && (
              <div className="an-metric-subtext">{userMetrics.activeUsers} active · {filteredQueries.length} total runs</div>
            )}
          </div>

          {/* Top user */}
          <div className="an-metric-card an-metric-card--large">
            <div className="an-metric-label">Top user</div>
            {loading ? (
              <div className="an-skeleton" style={{ width: 120, height: 36, display: 'block' }} />
            ) : userRows.length > 0 ? (
              <>
                <div className="an-metric-value" style={{ fontSize: 20, letterSpacing: 0 }}>
                  {userRows[0].id}
                </div>
                <div className="an-metric-subtext" style={{ marginTop: 4 }}>
                  {userRows[0].runs} runs · {userRows[0].sharePct}% of total
                </div>
                <div className="an-metric-subtext">
                  {userRows[0].longRunning > 0 && <span style={{ color: '#C46A05' }}>{userRows[0].longRunning} long-running · </span>}
                  {userRows[0].isService ? 'service account' : 'human'}
                </div>
              </>
            ) : (
              <div className="an-metric-value" style={{ fontSize: 20 }}>—</div>
            )}
          </div>
        </div>

        {/* Users table — all rows, scrollable; click a row to see that user's queries */}
        <div className="an-card">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div>
              <div className="an-card-title">All users</div>
              <div className="an-card-sub">{userRows.length} user{userRows.length !== 1 ? 's' : ''} · ranked by queries in range</div>
            </div>
            {selectedUserId && (
              <button className="an-user-clear-btn" onClick={() => setSelectedUserId(null)}>
                Clear selection
              </button>
            )}
          </div>

          {loading ? (
            <div className="an-skeleton" style={{ height: 260, borderRadius: 8 }} />
          ) : userRows.length === 0 ? (
            <div className="an-empty" style={{ padding: '24px 0' }}>No user data in period</div>
          ) : (
            <>
              <div className="an-user-scroll">
                <div className="an-user-table-head">
                  <span className="an-user-col-user">USER</span>
                  <span className="an-user-col-pct">%</span>
                  <span className="an-user-col-num">RUNS</span>
                  <span className="an-user-col-num" style={{ color: '#C46A05' }}>LONG RUN</span>
                  <span className="an-user-col-versions">VERSIONS</span>
                  <span className="an-user-col-last">LAST SEEN</span>
                </div>
                {userRows.map((row) => {
                  const diffMs = Date.now() - row.lastSeenMs;
                  const diffM  = Math.floor(diffMs / 60_000);
                  const lastSeen = !row.lastSeenMs ? '—'
                    : diffM < 60   ? `${diffM}m ago`
                    : diffM < 1440 ? `${Math.floor(diffM / 60)}h ago`
                    : `${Math.floor(diffM / 1440)}d ago`;
                  const isSelected = selectedUserId === row.id;

                  return (
                    <div
                      key={row.id}
                      className={`an-user-row${isSelected ? ' an-user-row--selected' : ''}`}
                      onClick={() => setSelectedUserId(isSelected ? null : row.id)}
                      title="Click to view queries"
                    >
                      <div className="an-user-col-user">
                        <span className="an-user-id" title={row.id}>{row.id}</span>
                      </div>
                      <span className="an-user-col-pct">{row.sharePct}%</span>
                      <span className="an-user-col-num">{row.runs}</span>
                      <span className="an-user-col-num" style={{ color: row.longRunning > 0 ? '#C46A05' : '#A9A3B8' }}>
                        {row.longRunning || 0}
                      </span>
                      <span className="an-user-col-versions">
                        {row.versions.map((v: string) => `v${v}`).join(' ')}
                      </span>
                      <span className="an-user-col-last">{lastSeen}</span>
                    </div>
                  );
                })}
              </div>

              {/* Query detail panel — shown when a user row is selected */}
              {selectedUserId && (() => {
                const userQueries = filteredQueries
                  .filter(q => (q.user_id ?? q.instance_id ?? 'unknown') === selectedUserId)
                  .sort((a, b) => b.timestamp - a.timestamp);
                return (
                  <div className="an-user-query-panel">
                    <div className="an-user-query-panel-header">
                      <span className="an-user-query-panel-title">
                        Queries by <span className="an-user-query-panel-user">{selectedUserId}</span>
                      </span>
                      <span className="an-user-query-panel-count">{userQueries.length} queries</span>
                    </div>
                    <div className="an-user-query-list">
                      {userQueries.length === 0 ? (
                        <div className="an-empty" style={{ padding: '16px 0' }}>No queries found</div>
                      ) : userQueries.map((q) => {
                        const ts = new Date(q.timestamp);
                        const tsStr = ts.toLocaleString();
                        return (
                          <div key={q.query_id} className="an-user-query-row">
                            <div className="an-user-query-meta">
                              <span className={`an-user-query-status an-user-query-status--${q.status.toLowerCase()}`}>
                                {q.status}
                              </span>
                              {q.long_running && (
                                <span className="an-user-query-long">LONG</span>
                              )}
                              <span className="an-user-query-time">{tsStr}</span>
                              <span className="an-user-query-id">{q.query_id}</span>
                            </div>
                            <pre className="an-user-query-text">{q.query_string || '—'}</pre>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Heatmap — full width */}
        <div className="an-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div className="an-card-title">When the graph is used</div>
              <div className="an-card-sub">Queries by weekday and two-hour block · {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
            </div>
            <div className="an-heatmap-legend">
              <span className="an-heatmap-legend-label">low</span>
              {['#F4F1F8', '#E8DAFB', '#CBA8F3', '#A96DEC', '#8E42EE'].map(c => (
                <span key={c} className="an-heatmap-swatch" style={{ background: c }} />
              ))}
              <span className="an-heatmap-legend-label">high</span>
            </div>
          </div>
          {loading ? (
            <div className="an-skeleton" style={{ height: 200, borderRadius: 8 }} />
          ) : (
            <Heatmap matrix={heatmapData.matrix} users={heatmapData.users} max={heatmapData.max} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
